import { Lifecycle } from './lifecycles.model';
import { SbefScopeGroupKey } from '../construction/sbef-custom-groups.data';

export const BUILTIN_SCOPE_IDS = [
  'boverket',
  'boverket-typical',
  'breeam-a1-a5',
  'breeam-full',
  'miljobyggnad-silver',
  'miljobyggnad-guld',
] as const;
export type ScopePresetId =
  (typeof BUILTIN_SCOPE_IDS)[number] | `saved:${string}`;

/** Type of generic (non-EPD) climate data a scope is based on. */
export type GenericClimateData = 'typical' | 'conservative';

/**
 * Byggnadstyp a limit value depends on. Every published Swedish limit value is
 * given per building type, so a target is a full table rather than one number.
 * Only types a Nodon project can actually be are listed — every activity maps to
 * one, see TARGET_BUILDING_TYPE_BY_ACTIVITY. Both Miljöbyggnad 4.1 (tabell 4.1)
 * and BREEAM-SE v6 (Mat 01) publish further rows (småhus, förskolor,
 * logistikhallar, specialbostäder) that no activity can produce today; add the
 * row together with the activity that needs it.
 */
export enum TargetBuildingType {
  MultiDwelling = 'multi_dwelling',
  Office = 'office',
  School = 'school',
  /** "Övriga byggnadstyper" — every activity without a row of its own. */
  Other = 'other',
}

/** kgCO2e per m² gross floor area (GFA/BTA), for every byggnadstyp. */
export type KgCo2ePerM2GfaByBuildingType = Record<TargetBuildingType, number>;

/**
 * A climate target belonging to a scope. Certifications grade the same scope at
 * several levels (Miljöbyggnad Silver vs Guld), so a scope carries a list rather
 * than a single target.
 *
 * The two kinds mirror how the systems express their requirement. Miljöbyggnad
 * publishes the limit value itself. BREEAM publishes none: it awards points for
 * a percentage improvement on a reference value, so its targets stay relative
 * and resolve against {@link ScopePreset.referenceValues} — which lets a project
 * override that reference (bespoke or ombyggnad, Mat 01 ref 2.0 and M2) and have
 * every point level follow.
 */
export type ScopeTarget = {
  id: string;
  /** Level name, shown next to the scope name ("Silver", "Guld"). */
  targetName: string;
  /**
   * The level name without the qualifier {@link targetName} spells out, for
   * places that already carry the number it stands for ("1 credit" beside its
   * own limit value, rather than "1 credit (10 %)"). Omit when the name has no
   * qualifier to drop.
   */
  shortTargetName?: string;
  /**
   * Lifecycle modules the limit value applies to, when they are narrower than
   * what the scope reports. Miljöbyggnad Guld is the case it exists for: the
   * whole lifecycle A–C must be calculated, but the limit value in tabell 4.1
   * grades A1–A5 alone. Omit to grade everything the scope counts.
   */
  lifecycles?: Lifecycle[];
} & (
  | { kind: 'absolute'; kgCo2ePerM2Gfa: KgCo2ePerM2GfaByBuildingType }
  | { kind: 'reduction'; reduction: number }
);

export type ScopePreset = {
  id: ScopePresetId;
  label: string;
  /** Lifecycle phases enabled by the preset. */
  lifecycles: Lifecycle[];
  /** SBEF scope groups enabled by the preset. Empty means "all groups" (no filter). */
  sbefGroups: SbefScopeGroupKey[];
  /** Generic climate data convention (conservative applies the +25% uplift). */
  genericClimateData: GenericClimateData;
  /**
   * Calculation period in years. Undefined leaves the project's `building_lifetime`
   * untouched (only relevant for full-lifecycle scopes with B/C phases).
   */
  calculationPeriodYears?: number;
  /**
   * Climate targets the scope can be graded against, best (lowest) first.
   * Omitted for scopes that only prescribe what to count, not a limit value.
   */
  targets?: ScopeTarget[];
  /** Default target captured with an organization scope. */
  target?: number;
  /**
   * Reference values a {@link ScopeTarget} of kind `reduction` is measured
   * against. Required by such a target, unused by absolute ones.
   */
  referenceValues?: KgCo2ePerM2GfaByBuildingType;
};
